import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useCallback, useRef, useState } from "react";
import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";

interface DrawingMetrics {
  area: number;
  lineLength: number;
}

const useMapDrawing = (map: mapboxgl.Map | null) => {
  const [metrics, setMetrics] = useState<DrawingMetrics>({
    area: 0,
    lineLength: 0,
  });
  const [drawInstance, setDrawInstance] = useState<MapboxDraw | null>(null);
  const lineMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const areaMarkersRef = useRef<mapboxgl.Marker[]>([]);

  const addPointerMarker = useCallback(
    (coordinates: [number, number]) => {
      if (!map) {
        console.error("Map is not available to add a marker.");
        return null;
      }
      return new mapboxgl.Marker({ color: "#ff7800" })
        .setLngLat(coordinates)
        .addTo(map);
    },
    [map]
  );

  const removePointerMarker = useCallback((markerInstance: mapboxgl.Marker) => {
    markerInstance.remove();
  }, []);

  const initializeDrawing = useCallback(() => {
    if (!map) return;

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        simple_select: {
          ...(MapboxDraw.modes.simple_select as any),
          dragMove() {},
        },
        direct_select: {
          ...(MapboxDraw.modes.direct_select as any),
          dragFeature() {},
        },
      },
    });

    setDrawInstance(draw);
    map.addControl(draw);

    const updateArea = (features: GeoJSON.Feature[]) => {
      try {
        if (!features || features.length === 0) {
          return;
        }
        areaMarkersRef.current.forEach((m) => m.remove());
        areaMarkersRef.current = [];

        const existingLayers = map.getStyle().layers;
        existingLayers?.forEach((layer) => {
          if (layer.id.startsWith("area-label-")) {
            map.removeLayer(layer.id);
            map.removeSource(layer.id);
          }
        });

        const polygonFeatures = features.filter(
          (feature) => feature.geometry.type === "Polygon"
        );

        if (polygonFeatures.length > 0) {
          if (map.getSource("polygon-fill-source")) {
            const source = map.getSource(
              "polygon-fill-source"
            ) as mapboxgl.GeoJSONSource;
            source.setData({
              type: "FeatureCollection",
              features: polygonFeatures,
            });
          } else {
            map.addSource("polygon-fill-source", {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: polygonFeatures,
              },
            });

            map.addLayer({
              id: "polygon-fill",
              type: "fill",
              source: "polygon-fill-source",
              layout: {},
              paint: {
                "fill-color": "#ff0000",
                "fill-opacity": 0.2,
              },
            });
          }
        }

        features.forEach((feature) => {
          if (feature.geometry.type === "Polygon") {
            const coords = feature.geometry?.coordinates;
            if (
              !coords ||
              !Array.isArray(coords) ||
              coords.length === 0 ||
              !Array.isArray(coords[0]) ||
              coords[0].length < 3
            ) {
              return;
            }

            const area = turf.area(feature); // m²
            const rounded = Math.round(area * 100) / 100 / 10000;
            setMetrics((prev) => ({ ...prev, area: rounded }));

            const center = turf.center(feature).geometry.coordinates;

            const zoom = map.getZoom();
            const baseOffset = 0.05;
            const scaleFactor = 1 / Math.pow(2, zoom * 0.8);
            const offset = baseOffset * scaleFactor;

            const offsetCenter: [number, number] = [
              center[0],
              center[1] - offset,
            ];

            const markerEl = document.createElement("div");
            markerEl.style.background = "rgba(0, 0, 0, 0.6)";
            markerEl.style.color = "#ffffff";
            markerEl.style.padding = "4px 8px";
            markerEl.style.borderRadius = "4px";
            markerEl.style.fontSize = "13px";
            markerEl.style.boxShadow = "0 0 6px rgba(0,0,0,0.3)";
            markerEl.innerText = `${rounded.toFixed(2)} ha`;

            const marker = new mapboxgl.Marker(markerEl)
              .setLngLat(offsetCenter)
              .addTo(map);

            areaMarkersRef.current.push(marker);
          }
        });
      } catch (err) {
        console.error("Polygon processing failed:", err);
      }
    };

    const updateLineLength = (features: GeoJSON.Feature[]) => {
      lineMarkersRef.current.forEach((m) => m.remove());
      lineMarkersRef.current = [];

      const existingLayers = map.getStyle().layers;
      existingLayers?.forEach((layer) => {
        if (
          layer.id.startsWith("start-point-") ||
          layer.id.startsWith("end-point-")
        ) {
          map.removeLayer(layer.id);
          map.removeSource(layer.id);
        }
      });

      features.forEach((feature, index) => {
        if (feature.geometry.type === "LineString") {
          const length = turf.length(feature, { units: "meters" });
          const lineGeometry = feature.geometry as GeoJSON.LineString;
          const coordinates = lineGeometry.coordinates;

          if (!coordinates || coordinates.length < 2) return;
          const lastCoord = coordinates[coordinates.length - 1];
          const startCoord = coordinates[0];
          const endCoord = coordinates[coordinates.length - 1];

          map.addSource(`start-point-${index}`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: startCoord,
              },
              properties: {},
            },
          });

          map.addLayer({
            id: `start-point-${index}`,
            type: "circle",
            source: `start-point-${index}`,
            paint: {
              "circle-radius": 3,
              "circle-color": "#33cc33",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });

          map.addSource(`end-point-${index}`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: endCoord,
              },
              properties: {},
            },
          });

          map.addLayer({
            id: `end-point-${index}`,
            type: "circle",
            source: `end-point-${index}`,
            paint: {
              "circle-radius": 3,
              "circle-color": "#ff3333",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });

          const markerEl = document.createElement("div");
          markerEl.style.background = "rgba(0, 0, 0, 0.6)";
          markerEl.style.color = "#ffffff";
          markerEl.style.padding = "4px 8px";
          markerEl.style.borderRadius = "8px";
          markerEl.style.fontSize = "13px";
          markerEl.style.boxShadow = "0 0 6px rgba(0,0,0,0.3)";
          markerEl.innerText = `${length.toFixed(2)} m`;

          const offset = 0.001;
          const offsetCoord: [number, number] = [
            lastCoord[0],
            lastCoord[1] - offset,
          ];

          const marker = new mapboxgl.Marker(markerEl)
            .setLngLat(offsetCoord as [number, number])
            .addTo(map);

          lineMarkersRef.current.push(marker);
        }
      });
    };

    const updateDrawing = () => {
      const data = draw.getAll();

      if (!data || !data.features) return;

      const polygonFeatures = data.features.filter(
        (f) =>
          f.geometry.type === "Polygon" &&
          Array.isArray(f.geometry.coordinates) &&
          Array.isArray(f.geometry.coordinates[0]) &&
          f.geometry.coordinates[0].length >= 3
      );

      const lineFeatures = data.features.filter(
        (f) =>
          f.geometry.type === "LineString" &&
          Array.isArray(f.geometry.coordinates) &&
          f.geometry.coordinates.length >= 2
      );

      const drawnPolygonIds = new Set(
        polygonFeatures.filter((f) => f.id != null).map((f) => f.id!.toString())
      );

      areaMarkersRef.current = areaMarkersRef.current.filter((marker) => {
        const id = marker.getElement().dataset?.id;
        if (!id || !drawnPolygonIds.has(id)) {
          marker.remove();
          return false;
        }
        return true;
      });

      const existingLayers = map.getStyle().layers;
      existingLayers?.forEach((layer) => {
        if (layer.id.startsWith("area-label-")) {
          const layerId = layer.id.replace("area-label-", "");
          if (!drawnPolygonIds.has(layerId)) {
            map.removeLayer(layer.id);
            map.removeSource(layer.id);
          }
        }
      });

      if (polygonFeatures.length > 0) {
        updateArea(polygonFeatures);
      } else {
        if (map.getLayer("polygon-fill")) {
          map.removeLayer("polygon-fill");
        }
        if (map.getSource("polygon-fill-source")) {
          map.removeSource("polygon-fill-source");
        }
        setMetrics((prev) => ({ ...prev, area: 0 }));
      }

      if (lineFeatures.length > 0) {
        updateLineLength(lineFeatures);
      } else {
        lineMarkersRef.current.forEach((m) => m.remove());
        lineMarkersRef.current = [];

        existingLayers?.forEach((layer) => {
          if (
            layer.id.startsWith("start-point-") ||
            layer.id.startsWith("end-point-")
          ) {
            if (map.getLayer(layer.id)) {
              map.removeLayer(layer.id);
            }
            if (map.getSource(layer.id)) {
              map.removeSource(layer.id);
            }
          }
        });
      }
    };

    map.on("draw.render", updateDrawing);
    map.on("draw.create", updateDrawing);
    map.on("draw.delete", updateDrawing);
    map.on("draw.update", updateDrawing);

    return () => {
      map.removeControl(draw);
    };
  }, [map]);

  return {
    metrics,
    initializeDrawing,
    drawInstance,
    addPointerMarker,
    removePointerMarker,
  };
};

export default useMapDrawing;
